import React, { useMemo, useState } from 'react';
import { Ban, Eye, MoreHorizontal, RotateCcw, Skull, Users, KeyRound, User } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import Modal from '../../ui/Modal';
import { LoadingSpinner } from '../../ui/Loading';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/Table';
import { Button as ShadButton } from '../../ui/shadcn';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/shadcn/dropdown-menu';
import { usePermission, useRole } from '../../../hooks/useAuth';
import AdminSetPasswordModal from '../../shared/AdminSetPasswordModal';
import AdminUpdateUsernameModal from '../../shared/AdminUpdateUsernameModal';
import { NumberedPagination, PageHeader, SortableTableHead, useTimezone, formatInTimezone } from '@kioskgaming/ui';
import useTableSort from '../../../hooks/useTableSort';
import { getTableSortColumns } from '../../../config/tableSortConfig';
import type { SortDirection } from '@kioskgaming/ui/dist/data-table/sortState';
import { cn } from '../../../utils';
import {
  useAdminAgentDetail,
  useAdminCashierReport,
  useAdminCashierShifts,
  useAdminCashiers,
  useAdminFraudIndicators,
  useCdnAgents,
  useSuspendAdminCashier,
  useUnsuspendAdminCashier,
  useTerminateAdminCashier,
} from '../../../hooks/useApi';
import type { AdminCashierListItem, CdnAgent, CdnShop } from '../../../types';

type Period = 'daily' | 'weekly' | 'monthly';

function Select({
  label,
  value,
  onChange,
  options,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium leading-none">{label}</label>
      <select
        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function statusLabel(status: string) {
  if (status === 'active') return 'Active';
  if (status === 'suspended') return 'Suspended';
  if (status === 'terminated') return 'Terminated';
  return status;
}

const CashierManagement: React.FC = () => {
  const { timezone } = useTimezone();
  const canManage = usePermission('agentManagement');
  const canManageCredentials = useRole(['system_admin', 'super_admin']);

  const [agentId, setAgentId] = useState<string>('');
  const [shopId, setShopId] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [q, setQ] = useState<string>('');
  const [page, setPage] = useState<number>(1);

  const tableSort = useTableSort({
    columns: getTableSortColumns('cashiers'),
    onChange: () => setPage(1),
  });

  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState<AdminCashierListItem | null>(null);
  const [period, setPeriod] = useState<Period>('daily');
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [usernameModalOpen, setUsernameModalOpen] = useState(false);

  const agentsQuery = useCdnAgents(canManage);
  const agents = useMemo<CdnAgent[]>(() => agentsQuery.data?.data ?? [], [agentsQuery.data]);

  const agentDetailQuery = useAdminAgentDetail(agentId || undefined, !!agentId && canManage);
  const shopsForAgent = useMemo<CdnShop[]>(() => agentDetailQuery.data?.data?.shops ?? [], [agentDetailQuery.data]);

  const listParams = useMemo(
    () => ({
      page,
      limit: 20,
      ...(agentId ? { agent_id: agentId } : {}),
      ...(shopId ? { shop_id: shopId } : {}),
      ...(status ? { status } : {}),
      ...(q.trim() ? { q: q.trim() } : {}),
      ...tableSort.sortParams,
    }),
    [agentId, page, q, shopId, status, tableSort.sortParams],
  );

  const cashiersQuery = useAdminCashiers(listParams, canManage);
  const suspendMutation = useSuspendAdminCashier();
  const unsuspendMutation = useUnsuspendAdminCashier();
  const terminateMutation = useTerminateAdminCashier();

  const reportQuery = useAdminCashierReport(
    selected?.id,
    { period, ...(from ? { from } : {}), ...(to ? { to } : {}) },
    detailOpen && !!selected,
  );
  const shiftsQuery = useAdminCashierShifts(
    selected?.id,
    { ...(from ? { from } : {}), ...(to ? { to } : {}) },
    detailOpen && !!selected,
  );
  const fraudQuery = useAdminFraudIndicators(
    {
      period,
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
      ...(selected?.shopId ? { shop_id: selected.shopId } : {}),
    },
    detailOpen && !!selected,
  );

  const total = cashiersQuery.data?.meta?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 20));

  const appliedSort = useMemo(
    () => ({
      sortBy: (cashiersQuery.data?.meta?.sortBy ?? null) as string | null,
      sortOrder: (cashiersQuery.data?.meta?.sortOrder ?? null) as SortDirection | null,
    }),
    [cashiersQuery.data?.meta?.sortBy, cashiersQuery.data?.meta?.sortOrder],
  );

  const headerSort = tableSort.sortBy
    ? {
        activeKey: appliedSort.sortBy ?? tableSort.sortBy,
        direction: appliedSort.sortOrder ?? tableSort.sortOrder,
      }
    : { activeKey: null, direction: null };

  const isTableBusy = cashiersQuery.isFetching && (cashiersQuery.data?.data?.length ?? 0) > 0;

  // Cashier creation from Admin is disabled.

  const onChangeAgent = (v: string) => {
    setAgentId(v);
    setShopId('');
    setPage(1);
  };

  const onChangeShop = (v: string) => {
    setShopId(v);
    setPage(1);
  };

  // Cashier creation from Admin is disabled.

  const openDetails = (c: AdminCashierListItem) => {
    setSelected(c);
    setPeriod('daily');
    setFrom('');
    setTo('');
    setDetailOpen(true);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Cashier Management"
        description="Manage cashiers across shops. View reports, shifts, and fraud indicators."
        titleAddon={<Users className="h-7 w-7 text-primary" aria-hidden />}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-4">
          <Select
            label="Agent"
            value={agentId}
            onChange={onChangeAgent}
            options={[
              { value: '', label: 'All agents' },
              ...agents.map((a) => ({ value: a.id, label: a.name || a.email || a.id })),
            ]}
            disabled={!canManage || agentsQuery.isLoading}
          />
          <Select
            label="Shop"
            value={shopId}
            onChange={onChangeShop}
            options={[
              { value: '', label: agentId ? 'All shops (this agent)' : 'All shops' },
              ...shopsForAgent.map((s) => ({ value: s.id, label: s.name || s.email || s.id })),
            ]}
            disabled={!canManage || (!!agentId && agentDetailQuery.isLoading)}
          />
          <Select
            label="Status"
            value={status}
            onChange={(v) => {
              setStatus(v);
              setPage(1);
            }}
            options={[
              { value: '', label: 'All statuses' },
              { value: 'active', label: 'Active' },
              { value: 'suspended', label: 'Suspended' },
              { value: 'terminated', label: 'Terminated' },
            ]}
            disabled={!canManage}
          />
          <Input
            label="Search"
            placeholder="Search by username…"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            disabled={!canManage}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Cashiers</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {cashiersQuery.isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : cashiersQuery.error ? (
            <div className="p-6 text-destructive">Failed to load cashiers.</div>
          ) : (
            <div
              className={cn(
                'overflow-x-auto transition-opacity',
                isTableBusy && 'opacity-60',
              )}
              aria-busy={isTableBusy || undefined}
            >
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTableHead
                      sortable
                      label="Username"
                      sortKey="username"
                      activeKey={headerSort.activeKey}
                      direction={headerSort.direction}
                      onSort={tableSort.toggleSort}
                    />
                    <TableHead>Agent</TableHead>
                    <TableHead>Shop</TableHead>
                    <TableHead>Status</TableHead>
                    <SortableTableHead
                      sortable
                      label="Last login"
                      sortKey="lastLoginAt"
                      activeKey={headerSort.activeKey}
                      direction={headerSort.direction}
                      onSort={tableSort.toggleSort}
                    />
                    <TableHead className="text-right">Tx today</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(cashiersQuery.data?.data ?? []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                        No cashiers found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    (cashiersQuery.data?.data ?? []).map((c) => (
                      <TableRow key={c.id}>
                        <TableCell className="font-medium">{c.username}</TableCell>
                        <TableCell>{c.agent?.name ?? '—'}</TableCell>
                        <TableCell>{c.shop?.name ?? '—'}</TableCell>
                        <TableCell>
                          <span className="uppercase tracking-wide">{statusLabel(c.status)}</span>
                        </TableCell>
                        <TableCell className="tabular-nums">
                          {c.lastLoginAt ? formatInTimezone(c.lastLoginAt, timezone) : '—'}
                        </TableCell>
                        <TableCell className="text-right font-mono">{c.totalTransactionsToday}</TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <ShadButton variant="outline" size="sm" className="h-8 w-8 p-0" aria-label="Open actions">
                                <MoreHorizontal className="h-4 w-4" aria-hidden />
                              </ShadButton>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="min-w-[160px]">
                              <DropdownMenuItem onSelect={() => openDetails(c)}>
                                <Eye className="text-muted-foreground" aria-hidden />
                                View
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                disabled={
                                  c.status !== 'active' ||
                                  suspendMutation.isLoading ||
                                  unsuspendMutation.isLoading ||
                                  terminateMutation.isLoading
                                }
                                onSelect={() => suspendMutation.mutate(c.id)}
                              >
                                <Ban className="text-muted-foreground" aria-hidden />
                                Suspend
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                disabled={
                                  c.status !== 'suspended' ||
                                  suspendMutation.isLoading ||
                                  unsuspendMutation.isLoading ||
                                  terminateMutation.isLoading
                                }
                                onSelect={() => unsuspendMutation.mutate(c.id)}
                              >
                                <RotateCcw className="text-muted-foreground" aria-hidden />
                                Unsuspend
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                disabled={
                                  c.status === 'terminated' ||
                                  terminateMutation.isLoading ||
                                  suspendMutation.isLoading ||
                                  unsuspendMutation.isLoading
                                }
                                onSelect={() => terminateMutation.mutate(c.id)}
                              >
                                <Skull className="text-destructive" aria-hidden />
                                Terminate
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}

          <div className="flex items-center justify-between gap-3 border-t border-border px-6 py-4">
            <div className="text-sm text-muted-foreground">
              Page {page} / {totalPages} • Total {total}
            </div>
            <NumberedPagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              variant="admin"
              prevLabel="Prev"
              nextLabel="Next"
            />
          </div>
        </CardContent>
      </Card>

      {/* Cashier creation from Admin is disabled */}

      <Modal
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        title={selected ? `Cashier: ${selected.username}` : 'Cashier'}
        size="lg"
        variant="default"
        scrollable
        secondaryAction={{ label: 'Close', onClick: () => setDetailOpen(false) }}
      >
        {!selected ? null : (
          <div className="space-y-6">
            {canManageCredentials && selected.status !== 'terminated' ? (
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full sm:w-auto justify-center"
                  onClick={() => setUsernameModalOpen(true)}
                >
                  <User className="h-4 w-4 mr-1.5 shrink-0" />
                  Change username
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full sm:w-auto justify-center"
                  onClick={() => setPasswordModalOpen(true)}
                >
                  <KeyRound className="h-4 w-4 mr-1.5 shrink-0" />
                  Reset password
                </Button>
              </div>
            ) : null}
            <div className="grid gap-4 sm:grid-cols-4">
              <Select
                label="Period"
                value={period}
                onChange={(v) => setPeriod(v as Period)}
                options={[
                  { value: 'daily', label: 'Daily' },
                  { value: 'weekly', label: 'Weekly' },
                  { value: 'monthly', label: 'Monthly' },
                ]}
              />
              <Input label="From (ISO)" placeholder="2026-04-01T00:00:00Z" value={from} onChange={(e) => setFrom(e.target.value)} />
              <Input label="To (ISO)" placeholder="2026-04-30T23:59:59Z" value={to} onChange={(e) => setTo(e.target.value)} />
              <div className="flex items-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setFrom('');
                    setTo('');
                  }}
                >
                  Reset range
                </Button>
              </div>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Report</CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                {reportQuery.isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <LoadingSpinner />
                  </div>
                ) : reportQuery.error ? (
                  <div className="text-destructive">Failed to load report.</div>
                ) : (
                  <div className="grid gap-3 sm:grid-cols-3">
                    <div className="rounded-md border border-border p-3">
                      <div className="text-muted-foreground">Deposits</div>
                      <div className="text-lg font-semibold font-mono">
                        {reportQuery.data?.data?.totals?.deposits?.total ?? 0}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Count: {reportQuery.data?.data?.totals?.deposits?.count ?? 0}
                      </div>
                    </div>
                    <div className="rounded-md border border-border p-3">
                      <div className="text-muted-foreground">Redemptions</div>
                      <div className="text-lg font-semibold font-mono">
                        {reportQuery.data?.data?.totals?.redemptions?.total ?? 0}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Count: {reportQuery.data?.data?.totals?.redemptions?.count ?? 0}
                      </div>
                    </div>
                    <div className="rounded-md border border-border p-3">
                      <div className="text-muted-foreground">Withdrawals</div>
                      <div className="text-lg font-semibold font-mono">
                        {reportQuery.data?.data?.totals?.withdrawals?.total ?? 0}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Count: {reportQuery.data?.data?.totals?.withdrawals?.count ?? 0}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Shift history</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {shiftsQuery.isLoading ? (
                  <div className="flex items-center justify-center py-10">
                    <LoadingSpinner />
                  </div>
                ) : shiftsQuery.error ? (
                  <div className="p-6 text-destructive">Failed to load shifts.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Opened</TableHead>
                          <TableHead>Closed</TableHead>
                          <TableHead className="text-right">Opening</TableHead>
                          <TableHead className="text-right">Expected</TableHead>
                          <TableHead className="text-right">Counted</TableHead>
                          <TableHead className="text-right">Discrepancy</TableHead>
                          <TableHead>Flag</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(shiftsQuery.data?.data ?? []).length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                              No shifts found.
                            </TableCell>
                          </TableRow>
                        ) : (
                          (shiftsQuery.data?.data ?? []).map((s) => (
                            <TableRow key={s.id}>
                              <TableCell className="tabular-nums">{formatInTimezone(s.openedAt, timezone)}</TableCell>
                              <TableCell className="tabular-nums">{s.closedAt ? formatInTimezone(s.closedAt, timezone) : '—'}</TableCell>
                              <TableCell className="text-right font-mono">{s.openingCashBalance ?? 0}</TableCell>
                              <TableCell className="text-right font-mono">{s.expectedClosingCash ?? '—'}</TableCell>
                              <TableCell className="text-right font-mono">{s.closingCashCounted ?? '—'}</TableCell>
                              <TableCell className="text-right font-mono">{s.discrepancyAmount ?? '—'}</TableCell>
                              <TableCell>{s.discrepancyFlagged ? 'Yes' : 'No'}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Fraud indicators (shop scope)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                {fraudQuery.isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <LoadingSpinner />
                  </div>
                ) : fraudQuery.error ? (
                  <div className="text-destructive">Failed to load fraud indicators.</div>
                ) : (
                  <>
                    <div className="grid gap-3 sm:grid-cols-3">
                      <div className="rounded-md border border-border p-3">
                        <div className="text-muted-foreground">High redemptions</div>
                        <div className="text-lg font-semibold font-mono">{fraudQuery.data?.data?.highRedemptions?.length ?? 0}</div>
                      </div>
                      <div className="rounded-md border border-border p-3">
                        <div className="text-muted-foreground">Discrepancy shifts</div>
                        <div className="text-lg font-semibold font-mono">{fraudQuery.data?.data?.discrepancies?.length ?? 0}</div>
                      </div>
                      <div className="rounded-md border border-border p-3">
                        <div className="text-muted-foreground">Outside shift hours</div>
                        <div className="text-lg font-semibold font-mono">{fraudQuery.data?.data?.outsideShiftHours?.length ?? 0}</div>
                      </div>
                    </div>

                    <div className="rounded-md border border-border p-3">
                      <div className="font-medium mb-2">High redemptions</div>
                      {(fraudQuery.data?.data?.highRedemptions ?? []).length === 0 ? (
                        <div className="text-muted-foreground">None.</div>
                      ) : (
                        <ul className="list-disc pl-5 space-y-1">
                          {(fraudQuery.data?.data?.highRedemptions ?? []).slice(0, 10).map((x) => (
                            <li key={x.cashierId}>
                              Cashier {x.cashierId}: totalRedeem {x.totalRedeem}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </Modal>

      {canManageCredentials && selected && selected.status !== 'terminated' ? (
        <>
          <AdminSetPasswordModal
            open={passwordModalOpen}
            onClose={() => setPasswordModalOpen(false)}
            target={{ kind: 'cashier', cashierId: selected.id }}
            minLength={6}
            title={`Reset password: ${selected.username}`}
            onSuccess={() => {
              toast.success('Cashier password updated. Sessions were signed out.');
            }}
          />
          <AdminUpdateUsernameModal
            open={usernameModalOpen}
            onClose={() => setUsernameModalOpen(false)}
            target={{ kind: 'cashier', cashierId: selected.id }}
            currentUsername={selected.username}
            title="Change cashier username"
            onSuccess={(res) => {
              const nextUsername =
                (res as { data?: { username?: string } })?.data?.username ||
                (res as { username?: string })?.username;
              if (nextUsername) {
                setSelected((prev) => (prev ? { ...prev, username: nextUsername } : prev));
              }
              toast.success('Cashier username updated. Sessions were signed out.');
              void cashiersQuery.refetch();
            }}
          />
        </>
      ) : null}
    </div>
  );
};

export default CashierManagement;

